<script lang="ts">
	import { sanitizeInputs } from '$lib/util/util';
	import {
		stopLossInput,
		isStopLossInputValid,
		longOrShort,
		estimatedLoss,
		executionType,
		triggerInputForTrade,
		lossPercentage,
		stopLossErrorMessage,
		isStopLossInputSafe,
		stopLossWarningMessage,
		stopLossErrorDescription
	} from '../store.js';
	import {
		currentMarket,
		currentPosition,
		loadingPostTradeDetails,
		postTradeDetails
	} from '../../store.js';
	import BigNumber from 'bignumber.js';
	import { onDestroy, onMount } from 'svelte';
	import {
		DEFAULT_STOP_LOSS_PERCENTAGE,
		LIQUIDATION_PRICE_GAP_PERCENTAGE
	} from '../../constants.js';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import { marketsConfig } from '$lib/config/markets.js';
	import { fly } from 'svelte/transition';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';

	let currentPriceLimit: string;
	let currentPriceTrigger: string;

	$: currentPriceLimit = $currentMarket
		? $executionType != 'Market'
			? $triggerInputForTrade
			: $currentMarket.safePrice
		: '0';

	$: currentPriceTrigger = $currentMarket
		? $executionType != 'Market'
			? $triggerInputForTrade
			: $currentMarket.safePrice
		: '0';

	let _isStopLossInputValid = false;
	let _isStopLossInputSafe = false;
	let _stopLossErrorMessage = '';
	let _stopLossWarningMessage = '';

	let lossPercentageEventListner = () => {};
	let stopLossEventListner = () => {};

	$: _isStopLossInputValid = $isStopLossInputValid;
	$: _isStopLossInputSafe = $isStopLossInputSafe;
	$: _stopLossErrorMessage = $stopLossErrorMessage;
	$: _stopLossWarningMessage = $stopLossWarningMessage;

	onMount(() => {
		_lossPercentage = getDefaultStopLossPercenatge();
		lossPercentage.set(_lossPercentage);

		handleLossPercentageInput();

		lossPercentageEventListner = currentMarket.subscribe((val) => {
			val && syncLossPercentageValue();
			lossPercentage.set(_lossPercentage);
		});
	});

	const syncStopLossValue = () => {
		if (!currentPriceTrigger || new BigNumber(_lossPercentage).isNaN()) {
			_stopLossInput = '';
			return;
		}

		_stopLossInput = $currentMarket
			? $longOrShort === 'Long'
				? new BigNumber(1)
						.minus(new BigNumber(_lossPercentage).div(100))
						.multipliedBy(currentPriceTrigger)
						.div(1e18)
						.toFixed(marketsConfig[$currentMarket.asset].showDecimals + 2)
						.toString()
				: new BigNumber(1)
						.plus(new BigNumber(_lossPercentage).div(100))
						.multipliedBy(currentPriceTrigger)
						.div(1e18)
						.toFixed(marketsConfig[$currentMarket.asset].showDecimals + 2)
						.toString()
			: '0';
	};

	const syncLossPercentageValue = () => {
		_lossPercentage =
			currentPriceTrigger && new BigNumber(_stopLossInput).isGreaterThan(0)
				? $longOrShort === 'Long'
					? new BigNumber(currentPriceTrigger)
							.minus(new BigNumber(_stopLossInput).multipliedBy(1e18))
							.div(currentPriceTrigger)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
					: new BigNumber(_stopLossInput)
							.multipliedBy(1e18)
							.minus(currentPriceTrigger)
							.div(currentPriceTrigger)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
				: '';
	};

	const handleStopLossInput = () => {
		_stopLossInput = sanitizeInputs(_stopLossInput);
		stopLossInput.set(_stopLossInput || '0');
		syncLossPercentageValue();
		lossPercentage.set(_lossPercentage || '0');
	};

	const handleLossPercentageInput = () => {
		_lossPercentage = sanitizeInputs(_lossPercentage);
		lossPercentage.set(_lossPercentage);
		syncStopLossValue();
		stopLossInput.set(_stopLossInput || '0');
	};

	const onLossInputFocus = () => {
		lossPercentageEventListner();
		stopLossEventListner();
		stopLossEventListner = currentMarket.subscribe((val) => {
			if (val) {
				syncStopLossValue();
				stopLossInput.set(_stopLossInput || '0');
			}
		});
	};

	const onStopLossInputFocus = () => {
		stopLossEventListner();
		lossPercentageEventListner();
		lossPercentageEventListner = currentMarket.subscribe((val) => {
			if (val) {
				syncLossPercentageValue();
				lossPercentage.set(_lossPercentage || '0');
			}
		});
	};

	const getDefaultStopLossPercenatge = () => {
		const liquidationPrice = $postTradeDetails
			? $postTradeDetails.liquidationPrice
			: $currentPosition
			? $currentPosition?.liquidationPrice
			: 0;
		let stopLossSafePrice;

		if ($longOrShort === 'Long') {
			stopLossSafePrice = new BigNumber(liquidationPrice)
				.multipliedBy(
					new BigNumber(1).plus(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE).div(100))
				)
				.toFixed(2);
		} else {
			stopLossSafePrice = new BigNumber(liquidationPrice)
				.multipliedBy(
					new BigNumber(1).minus(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE).div(100))
				)
				.toFixed(2);
		}

		const stopLossPercentage = new BigNumber(stopLossSafePrice)
			.minus(new BigNumber(currentPriceTrigger))
			.abs()
			.div(new BigNumber(currentPriceTrigger))
			.multipliedBy(100)
			.toFixed(2)
			.toString();
		if (new BigNumber(stopLossPercentage).lt(DEFAULT_STOP_LOSS_PERCENTAGE)) {
			return stopLossPercentage;
		}
		return DEFAULT_STOP_LOSS_PERCENTAGE;
	};

	let _stopLossInput: string;
	let _lossPercentage: string;

	onDestroy(() => {
		stopLossInput.set('0');
		lossPercentageEventListner();
		stopLossEventListner();
	});
</script>

<div class="flex gap-2 mt-3">
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!_isStopLossInputValid || !_isStopLossInputSafe}
		class:border-fx-red={!_isStopLossInputValid}
		class:border-fx-yellow={_isStopLossInputValid && !_isStopLossInputSafe}
	>
		<div class="flex-1 flex gap-1.5">
			<img
				loading="lazy"
				src={'./less_than_icon.svg'}
				alt="less than icon"
				class="w-2.5"
				class:-scale-x-100={$longOrShort == 'Long'}
			/>
			<div class="flex items-center">
				<span class="text-xs">$</span>

				<input
					inputmode="decimal"
					autocomplete="off"
					autocorrect="off"
					pattern="^[0-9]*[.,]?[0-9]*$"
					minlength="1"
					maxlength="79"
					spellcheck="false"
					type="string"
					placeholder="0"
					data-testid="open-position-amount"
					class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
					bind:value={_stopLossInput}
					on:input={handleStopLossInput}
					on:focus={onStopLossInputFocus}
				/>
			</div>
		</div>

		<div class="flex items-center gap-1 justify-center">
			{#if _isStopLossInputValid && !_isStopLossInputSafe}
				<Tooltip
					title="Polynomial is not responsible if SL is too close to liquidation and/or cannot be executed due to lack of fees in margin"
					header={_stopLossWarningMessage}
				>
					<div class="text-xxs text-fx-yellow flex items-center gap-2 justify-between w-full">
						<div class="text-xs text-fx-grey-400"><span class="mr-1">|</span>SL</div>
						<img
							loading="lazy"
							src={'/caution.png'}
							alt="Caution"
							class="w-3 inline-block animate-bounce"
						/>
					</div>
				</Tooltip>
			{:else}
				<div class="text-xs text-fx-grey-400">
					<span class="mr-1">|</span> Stop Loss
				</div>
			{/if}
		</div>
	</div>
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!_isStopLossInputValid || !_isStopLossInputSafe}
		class:border-fx-red={!_isStopLossInputValid}
		class:border-fx-yellow={_isStopLossInputValid && !_isStopLossInputSafe}
	>
		<div class="flex-1">
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_lossPercentage}
				on:input={handleLossPercentageInput}
				on:focus={onLossInputFocus}
			/>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Loss %</div>
	</div>
</div>
{#if !_isStopLossInputValid}
	{#if $stopLossErrorDescription != ''}
		<Tooltip bind:header={_stopLossErrorMessage} bind:title={$stopLossErrorDescription}>
			<div
				class="text-xxs text-fx-red mt-4 absolute font-semibold flex items-center gap-1 w-full"
				transition:fly={{ y: -10, duration: 200 }}
			>
				{_stopLossErrorMessage}
				<img loading="lazy" src={'/caution.png'} alt="Caution" class="w-2 inline-block" />
			</div>
		</Tooltip>
	{:else}
		<div
			class="text-xxs text-fx-red mt-1 absolute font-semibold flex items-center gap-1 w-full"
			transition:fly={{ y: -10, duration: 200 }}
		>
			{_stopLossErrorMessage}
			<img loading="lazy" src={'/caution.png'} alt="Caution" class="w-2 inline-block" />
		</div>
	{/if}
{/if}
<div class="flex justify-between mt-4">
	<div class="text-fx-grey-400 text-xs font-medium">Estimated Loss</div>
	<div class="flex gap-2">
		{#if $loadingPostTradeDetails}
			<ProcessingIcon />
		{/if}
		<div class="text-fx-gradient-22 text-xs font-medium">
			-${Math.abs(parseFloat($estimatedLoss)).toFixed(2)}
		</div>
	</div>
</div>

<style>
</style>
